import { finalize, first, map, shareReplay, tap } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { cloneDeep, sortBy, uniqBy } from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { Commodity, Crop, Field, Variety } from '../_models';
import { FieldService } from './field.service';
import { CropMetricsService } from './cropMetrics.service';
import { environment } from '../../../environments/environment';
import { AppState } from '../_reducers';
import { SupplyChainDestination } from '../_models/supplyChainDestination';
import { cropListMapping } from '../../../assets/data/enum/cropListMapping';
import { FarmService } from './farm.service';
import { selectAllCrops } from '../_selectors/crop.selectors';
import { loadFieldsSuccess, updateField, updateFieldSuccess } from '../_actions/field';
import { mergeCrops } from '../_actions/crop';
import { StreamChunk } from '../_models/bulkUpsert';
import { StreamService } from './stream.service';
import { XYearService } from './xYear.service';

@Injectable()
export class CropService {
  private apiBaseUrl = environment.apiBaseUrl;
  private cropList: Observable<any[]>;
  fieldList: BehaviorSubject<Array<Field>> = new BehaviorSubject<Array<Field>>([]);
  cropItemObj: BehaviorSubject<Crop> = new BehaviorSubject<Crop>(null);
  cropList$: BehaviorSubject<Array<Crop>> = new BehaviorSubject<Array<Crop>>(null);
  allCrop$: BehaviorSubject<Array<Crop>> = new BehaviorSubject<Array<Crop>>(null);
  supplyChainList$: BehaviorSubject<Array<SupplyChainDestination>> = new BehaviorSubject<
    Array<SupplyChainDestination>
  >(null);
  private supplyChainFetchInProgress$: Observable<SupplyChainDestination[]> | null = null;

  currentPreferredProductsCountry: string;

  constructor(
    private http: HttpClient,
    private cropMetricsService: CropMetricsService,
    private fieldService: FieldService,
    private store: Store<AppState>,
    private farmService: FarmService,
    private streamService: StreamService,
    private xYearService: XYearService
  ) {
    this.cropList = this.store.pipe(select(selectAllCrops));
    this.fieldList = this.fieldService.fieldList$;
    this.setInitPAC();
  }

  setInitPAC() {
    this.farmService.getPreferredProductsCountry().subscribe((pac) => {
      this.currentPreferredProductsCountry = pac;
    });
  }

  getFromLocalStore(): Observable<Crop[]> {
    return this.cropList;
  }

  getAndStoreCrops(data): Observable<Crop[]> {
    let simbol = '';
    let queryString = '';
    if (data && data.payload && data.payload.fieldId && data.payload.fieldId != null) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'field=' + data.payload.fieldId;
    }
    if (data && data.payload && data.payload.year) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'year=' + data.payload.year;
    }
    return this.http.get<Crop[]>(this.apiBaseUrl + 'api/private/v1/crops' + queryString).pipe(
      map((response) => {
        this.cropList$.next(response);
        this.allCrop$.next(response);
        this.resetFields(response);
        return response;
      })
    );
  }

  /**
   * This method is used to fetch crops from the server.
   * It makes a GET request to the 'api/private/v1/crops' endpoint.
   * The request can optionally include 'field' and 'year' parameters.
   *
   * @param {string} fieldId - The ID of the field for which crops are to be fetched. This is an optional parameter.
   * @param {number} year - The year for which crops are to be fetched. This is an optional parameter.
   * @param {number} baseYear - The year from which we want the crops onwards (it is ignored if year is present)
   * @param {boolean} multiYear - if true return only the multiyear crops
   *
   * @returns {Observable<Crop[]>} - An Observable that will emit an array of Crop objects.
   * Each Crop object represents a crop and includes all its related data.
   * If the server response status is not 200 or 201, an empty array will be returned.
   */
  getCrops(
    fieldId?: any,
    year?: number,
    baseYear?: number,
    multiYear?: boolean
  ): Observable<Crop[]> {
    let params = new HttpParams();
    if (fieldId) {
      params = params.set('field', fieldId);
    }

    if (year) {
      params = params.set('year', year);
    } else {
      //! baseYear can be used only in the absence of the year
      if (baseYear) {
        params = params.set('baseYear', baseYear);
      }
    }

    if (multiYear) {
      params = params.set('multiYear', multiYear);
    }
    return this.http
      .get<Crop[]>(this.apiBaseUrl + 'api/private/v1/crops', { params })
      .pipe(map((response) => response));
  }

  checkCrops(data): Observable<Crop[]> {
    let simbol = '';
    let queryString = '';
    if (data && data.payload && data.payload.fieldId && data.payload.fieldId != null) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'field=' + data.payload.fieldId;
    }
    if (data && data.payload && data.payload.year) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'year=' + data.payload.year;
    }
    return this.http
      .get<Crop[]>(this.apiBaseUrl + 'api/private/v1/crops' + queryString)
      .pipe(map((response) => response));
  }

  getCropsWithPagination(
    all: boolean = null,
    max: number = null,
    offset: number = null,
    sort: string = null,
    order: string = null
  ): Observable<Crop[]> {
    let params = new HttpParams();
    if (all != null) {
      params = params.set('all', all.toString());
    }
    if (max != null) {
      params = params.set('max', max.toString());
    }
    if (offset != null) {
      params = params.set('offset', offset.toString());
    }
    if (sort != null) {
      params = params.set('sort', sort);
    }
    if (order != null) {
      params = params.set('order', order);
    }

    return this.http
      .get<Crop[]>(`${this.apiBaseUrl}api/private/v1/crops`, { params })
      .pipe(map((response) => response as Crop[]));
  }

  mergeCrops(data): Observable<Crop[]> {
    let simbol = '';
    let queryString = '';
    if (data && !data.payload) {
      data = { ...data, payload: { fieldId: data.fieldId, year: data.year } };
    }
    if (data && data.payload && data.payload.fieldId) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'field=' + data.payload.fieldId;
    }
    if (data && data.payload && data.payload.year) {
      simbol = queryString.indexOf('?') !== -1 ? '&' : '?';
      queryString = queryString + simbol + 'year=' + data.payload.year;
    }
    return this.http.get<Crop[]>(this.apiBaseUrl + 'api/private/v1/crops' + queryString).pipe(
      map((response) => {
        this.updateCropList$(response, this.cropList$);
        this.updateCropList$(response, this.allCrop$);
        if (data && data.payload && data.payload.fieldId) {
          this.updateSingleField(data.payload.fieldId, response);
        }
        return response;
      })
    );
  }

  getVarieties(): Observable<Variety[]> {
    return this.http
      .get<Variety[]>(this.apiBaseUrl + 'api/private/v1/lists/varieties')
      .pipe(map((response) => response));
  }

  getVarietiesByCrops(): Observable<any[]> {
    let countryParam = '';
    if (
      this.currentPreferredProductsCountry &&
      this.currentPreferredProductsCountry !== undefined
    ) {
      countryParam = '?preferredProductsCountry=' + this.currentPreferredProductsCountry;
    }
    return this.http
      .get<any[]>(this.apiBaseUrl + 'api/private/v1/lists/varieties' + countryParam)
      .pipe(map((response) => response));
  }

  getVarietiesByCommodityId(idcommodity): Observable<any[]> {
    let countryParam = '';
    if (
      this.currentPreferredProductsCountry &&
      this.currentPreferredProductsCountry !== undefined
    ) {
      countryParam = '&preferredProductsCountry=' + this.currentPreferredProductsCountry;
    }
    return this.http
      .get<
        any[]
      >(this.apiBaseUrl + 'api/private/v1/lists/varieties?commodityId=' + idcommodity + countryParam)
      .pipe(map((response) => response));
  }

  /**
   * Fetches the supply chain destinations from the API and updates the supplyChainList$ BehaviorSubject.
   * This method is typically called internally when a fresh fetch is necessary.
   *
   * @returns Observable<SupplyChainDestination[]> - The fetched supply chain destinations.
   */
  getSupplyChainDestination(): Observable<SupplyChainDestination[]> {
    return this.http
      .get<SupplyChainDestination[]>(this.apiBaseUrl + 'api/private/v2/supplyChainDestination')
      .pipe(
        map((response) => {
          if (response) {
            // Update the BehaviorSubject with the latest data
            this.supplyChainList$.next(response);
          }
          // Return the response regardless
          return response;
        })
      );
  }

  /**
   * Retrieves the supply chains, either from cache or by initiating an API request.
   *
   * - If `forceFetch` is true, it always fetches fresh data from the API, updates the cache, and returns the new data.
   * - If `forceFetch` is false and cached data is available, it returns the cached data as an observable.
   * - If `forceFetch` is false and no data is cached, it triggers a new fetch and caches the response.
   * - If a fetch is already in progress, it returns the in-progress observable to avoid duplicate requests.
   *
   * @param forceFetch - Optional. Defaults to `false`. Set to `true` to bypass cache and fetch fresh data.
   * @returns Observable<SupplyChainDestination[]> - An observable that emits the supply chains.
   */
  getSupplyChains(forceFetch: boolean = false): Observable<SupplyChainDestination[]> {
    const currentValue = this.supplyChainList$.getValue();

    // Return cached data if available and no forceFetch is requested
    if (!forceFetch && currentValue?.length) {
      return this.supplyChainList$.asObservable();
    }

    // Return in-progress fetch if already in progress
    if (this.supplyChainFetchInProgress$) {
      return this.supplyChainFetchInProgress$;
    }

    // Start a new fetch
    this.supplyChainFetchInProgress$ = this.getSupplyChainDestination().pipe(
      tap((data) => {
        this.supplyChainList$.next(data); // Cache the response
      }),
      finalize(() => {
        this.supplyChainFetchInProgress$ = null;
      }),
      shareReplay(1)
    );

    return this.supplyChainFetchInProgress$;
  }

  addNewSupplyChaindestination(supplychainDestination: string) {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const supplychainDestinationObj = {
      key: supplychainDestination,
      name: supplychainDestination,
      companyId: companySelected ? companySelected.id : null,
    };

    return this.http
      .post(this.apiBaseUrl + 'api/private/v2/supplyChainDestination', supplychainDestinationObj)
      .pipe(
        map((response) => {
          this.getSupplyChainDestination().subscribe();
          return response;
        })
      );
  }

  addNewVariety(variety: Variety): Observable<Variety> {
    return this.http
      .post<Variety>(this.apiBaseUrl + 'api/private/v1/lists/varieties', variety)
      .pipe(map((response) => response));
  }

  uniqById(response) {
    const sortByResult = sortBy(response, ['name', 'id']);
    return uniqBy(sortByResult, 'name');
  }

  getCrop(id: number): Observable<Crop> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/crops/' + id)
      .pipe(map((response) => response));
  }

  /**
   * @description: Get the crop belonging to a Farm in you multiFarm
   * @param id{Number}
   * @param compnayId{string}
   * @returns {Observable<Crop>}
   */
  getCropByIdAndCompanyId(id: number, companyId): Observable<Crop> {
    return this.http
      .get<Crop>(this.apiBaseUrl + 'api/private/v1/crops/' + id + '?companyIds=' + companyId)
      .pipe(map((response) => response));
  }

  saveCrop(cropItem): Observable<Crop> {
    const crop = { ...cropItem };
    // Rimuovi lastUpdated e dateCreated
    delete crop.lastUpdated;
    delete crop.dateCreated;

    //? For transition to multiple supply chain destinations (aka supplyChainDestinations)
    //TODO: remove this when all the Saas will be migrated to the new supplyChainDestinations (no later than the end of 2025)
    delete crop.supplyChainDestination;

    // Trasforma commodity, field e plant in oggetti con solo l'id
    if (crop.commodity) {
      crop.commodity = { id: crop.commodity.id };
    }
    if (crop.field) {
      crop.field = { id: crop.field.id };
    }
    if (crop.plant) {
      crop.plant = { id: crop.plant.id };
    }

    // Imposta companyId
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    crop.companyId = companySelected ? companySelected.id : null;

    // Invia la richiesta HTTP
    if (crop.id) {
      return this.http.put<Crop>(`${this.apiBaseUrl}api/private/v2/crops/${crop.id}`, crop).pipe(
        map((response) => {
          // Esegui le azioni aggiuntive se la richiesta ha avuto successo
          this.handleSuccessResponse(crop.field.id);
          return response;
        })
      );
    } else {
      return this.http.post<Crop>(`${this.apiBaseUrl}api/private/v1/crops/`, crop).pipe(
        map((response) => {
          // Esegui le azioni aggiuntive se la richiesta ha avuto successo
          this.handleSuccessResponse(crop.field.id);
          return response;
        })
      );
    }
  }

  // Metodo privato per gestire la risposta di successo
  private handleSuccessResponse(fieldId: string): void {
    // Esegui le azioni aggiuntive qui
    this.store.dispatch(mergeCrops({ fieldId: +fieldId, year: null }));
    this.cropMetricsService.getCropMetrics().subscribe();
  }

  deleteCrop(crop: Crop): Observable<Crop> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/crops/' + crop.id).pipe(
      tap(() => {
        this.cropMetricsService.getCropMetrics().subscribe();
        this.deleteCrop$(crop, this.cropList$);
        this.deleteCrop$(crop, this.allCrop$);
        this.deleteCropFromSingleField(crop.field.id, crop);
      }),
      map(() => ({ id: crop.id }) as Crop)
    );
  }

  deleteMultiYearCrop(crop: Crop): Observable<Crop> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/multiyearcrops/' + crop.uuid).pipe(
      tap(() => {
        this.cropMetricsService.getCropMetrics().subscribe();
        this.deleteCrop$(crop, this.cropList$);
        this.deleteCrop$(crop, this.allCrop$);
        this.deleteCropFromSingleField(crop.field.id, crop);
      }),
      map(() => ({ id: crop.id }) as Crop)
    );
  }

  getCropsByCommodityIdAndYear = (id: number, year: number): Array<Crop> => {
    let listCrop = [];
    this.cropList
      .pipe(first((list: Crop[]) => list && list.length > 0))
      .subscribe((list: Crop[]) => {
        listCrop = list.filter((i) => i.commodity && i.commodity.id == id && i.year == year);
      });
    return listCrop;
  };

  getCropsByCommodityIdAndYearPromise = (id: number, year: number): Promise<Array<Crop>> => {
    return new Promise((resolve, reject) => {
      this.cropList.pipe(first((list: Crop[]) => list && list.length > 0)).subscribe(
        (list: Crop[]) => {
          const filteredList = list.filter(
            (i) => i.commodity && i.commodity.id == id && i.year == year
          );
          resolve(filteredList);
        },
        (error) => reject(error)
      );
    });
  };

  getLocalCrops = (): Array<Crop> => {
    let cropList: Array<Crop>;
    this.cropList
      .pipe(first((list: Crop[]) => list && list.length > 0))
      .subscribe((list: Crop[]) => {
        cropList = list;
      });
    return cropList;
  };

  getCropById = (id: number): Crop => {
    const list = this.allCrop$.getValue() || [];
    return list.find((i) => i.id === id) || null;
  };

  getCropSizeById = (id: number) => {
    const crop: Crop = this.getCropById(id);
    if (crop && crop.size) {
      return crop.size;
    } else {
      return null;
    }
  };

  getCropSizeByMultipleId = (ids: Array<any>) => {
    let sumsize = 0;
    if (ids && ids.length > 0) {
      ids.forEach((cropId) => {
        sumsize = sumsize + this.getCropSizeById(cropId);
      });
    }
    return sumsize;
  };

  getFieldIdAndNameByCropId = (id: number): object => {
    let item: Field;
    let fieldObj: any = null;
    const tempCrop: Crop = cloneDeep(this.getCropById(id));
    if (tempCrop && tempCrop.id) {
      this.fieldList
        .pipe(first((list: Field[]) => list && list.length > 0))
        .subscribe((list: Field[]) => {
          item = list.find((f) => f.id === tempCrop.field.id);
          if (item && item.id) {
            fieldObj = {
              id: item.id,
              name: item.name,
            };
          }
        });
    }
    return fieldObj;
  };

  getFieldIdNameAndSizeByCropId = (id: number): object => {
    let item: Field;
    let fieldObj: any = null;
    const tempCrop: Crop = cloneDeep(this.getCropById(id));
    if (tempCrop && tempCrop.id) {
      this.fieldList
        .pipe(first((list: Field[]) => list && list.length > 0))
        .subscribe((list: Field[]) => {
          item = list.find((f) => f.id === tempCrop.field.id);
          if (item && item.id) {
            fieldObj = {
              id: item.id,
              name: item.name,
              cropSize: tempCrop.size,
              fieldSize: item.size,
            };
          }
        });
    }
    return fieldObj;
  };

  getFieldIdNameAndSizeByFieldId = (id: number): object => {
    let item: Field;
    let fieldObj: any = null;
    if (id) {
      this.fieldList
        .pipe(first((list: Field[]) => list && list.length > 0))
        .subscribe((list: Field[]) => {
          item = list.find((i) => i.id === id);
          if (item && item.id) {
            fieldObj = {
              id: item.id,
              name: item.name,
              cropSize: null,
              fieldSize: item.size,
            };
          }
        });
    }
    return fieldObj;
  };
  getCropAndFieldByCropId = (id: number): Crop => {
    // tslint:disable-next-line:prefer-const
    const tempCrop: Crop = cloneDeep(this.getCropById(id));
    if (tempCrop && tempCrop.id) {
      const tmpFieldObjClone: Field = cloneDeep(this.fieldService.getFieldById(tempCrop.field.id));
      tempCrop.field = this.clearCropToFieldObject(tmpFieldObjClone);
    }
    return tempCrop;
  };

  getCropLabelMappings = (): any => {
    return new Observable((observer) => {
      observer.next(cropListMapping);
      observer.complete();
    });
  };

  clearCropToFieldObject = (field: any): any => {
    if (field?.crops) {
      delete field.crops;
    }
    return field;
  };
  getCropsOPModule(farmsAppend, selectedYear): Observable<Crop[]> {
    return this.http
      .get<
        Crop[]
      >(this.apiBaseUrl + 'api/private/v1/crops/' + farmsAppend + '&year=' + selectedYear)
      .pipe(
        // .map(response => response)
        map((response) => response)
      );
  }

  getCropsByFieldIdAndYear(fieldId, year): Crop[] {
    let filteredCrop: Crop[];
    this.cropList
      .pipe(first((list: Crop[]) => list && list.length > 0))
      .subscribe((list: Crop[]) => {
        filteredCrop = list.filter((i) => i.year === year && i.field.id === fieldId);
      });
    return filteredCrop;
  }

  initCropObj() {
    this.cropItemObj = new BehaviorSubject<Crop>(null);
  }

  getCropObj() {
    return this.cropItemObj;
  }

  setCropObj(crop: Crop) {
    this.cropItemObj.next(crop);
  }

  postBulkCrop(cropList): Observable<Crop[]> {
    //? For transition to multiple supply chain destinations (aka supplyChainDestinations)
    //TODO: remove this when all the Saas will be migrated to the new supplyChainDestinations (no later than the end of 2025)
    cropList.forEach((crop) => {
      delete crop.supplyChainDestination;
    });

    return this.http.post<Crop[]>(this.apiBaseUrl + 'api/private/v1/crops/bulk', cropList);
  }

  putBulkCrop(cropList): Observable<Crop[]> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;

    //? For transition to multiple supply chain destinations (aka supplyChainDestinations)
    //TODO: remove this when all the Saas will be migrated to the new supplyChainDestinations (no later than the end of 2025)
    cropList.forEach((crop) => {
      delete crop.supplyChainDestination;
    });

    return this.http.put<Crop[]>(
      this.apiBaseUrl + 'api/private/v1/crops/bulk/' + companyId,
      cropList
    );
  }

  deleteBulkCrop(cropList: Crop[]): Observable<Crop[]> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    const cropListId = cropList.map((crop) => crop.id);
    return this.http.delete<Crop[]>(this.apiBaseUrl + 'api/private/v1/crops/bulk/' + companyId, {
      body: cropListId,
    });
  }

  getCropListByUUID = (uuid: string): Crop[] => {
    let listFiltered = [];
    this.cropList
      .pipe(first((list: Crop[]) => list && list.length > 0))
      .subscribe((list: Crop[]) => {
        listFiltered = list.filter((i) => i.uuid === uuid);
      });
    return listFiltered;
  };

  resetFields = (crops: Array<Crop>) => {
    const fields = [];
    // Method that inserts the entire crop object into the field, so here we have the entire crop object
    this.fieldList.value.forEach((f: Field) => {
      // Cloniamo l'oggetto Field per renderlo editabile
      const clonedField = { ...f, crops: [] };

      crops.forEach((c) => {
        if (clonedField.id === c.field.id) {
          clonedField.crops.push(c);
        }
      });

      fields.push(clonedField);
    });
    this.store.dispatch(loadFieldsSuccess({ fields }));
  };

  updateSingleField = (fieldId, crops: Array<Crop>) => {
    const field = this.fieldService.getFieldById(fieldId);
    if (field && field.id && crops && crops.length > 0) {
      field.crops = crops;
    }
    this.updateFieldList$(field, this.fieldService.fieldList$);
    this.store.dispatch(updateFieldSuccess({ field }));
  };

  deleteCropFromSingleField = (fieldId, crop: Crop) => {
    const field = this.fieldService.getFieldById(fieldId);
    if (field && field.id && crop && crop.id && field.crops && field.crops.length > 0) {
      const existingIndex = field.crops.findIndex((c) => c.id === crop.id);
      if (existingIndex !== -1) {
        // Rimuovi la crop dall'array
        field.crops.splice(existingIndex, 1);
        // Aggiorna il field
        this.store.dispatch(updateField({ field }));
      }
    }
  };

  updateCropList$ = (newCrops: Array<Crop>, list$: BehaviorSubject<Crop[]>) => {
    const currentCrops = list$.getValue();

    // Aggiungi o aggiorna le crops nella lista esistente
    newCrops.forEach((newCrop) => {
      if (newCrop.year === this.xYearService.getYear()) {
        const existingIndex = currentCrops.findIndex((crop) => crop.id === newCrop.id);
        if (existingIndex !== -1) {
          // Se la crop esiste già, aggiornala
          currentCrops[existingIndex] = newCrop;
        } else {
          // Altrimenti, aggiungi la nuova crop
          currentCrops.push(newCrop);
        }
      }
    });

    // Aggiorna list$ con la lista aggiornata
    list$.next(currentCrops);
  };

  updateFieldList$ = (field: Field, list$: BehaviorSubject<Field[]>) => {
    const currentFields = list$.getValue();
    const existingIndex = currentFields.findIndex((f) => f.id === field.id);
    if (existingIndex !== -1) {
      currentFields[existingIndex] = field;
      list$.next(currentFields);
    }
  };

  deleteCrop$ = (item: Crop, list$: BehaviorSubject<Crop[]>) => {
    const currentCrops = list$.getValue();
    // Trova l'indice della crop da rimuovere
    const indexToRemove = currentCrops.findIndex((crop) => crop.id === item.id);

    if (indexToRemove !== -1) {
      // Rimuovi la crop dall'array
      currentCrops.splice(indexToRemove, 1);
      // Aggiorna list$ con l'array aggiornato
      list$.next(currentCrops);
    }
  };

  /**
   * Used to check whether a Parcel has a crop assigned to it
   *
   * @param {string} parcelId - The ID of the parcel to check
   * @param {string} fieldId - The ID of the Field which the parcel belongs to
   *
   * @returns {Observable<Crop[]>} - An Observable that will emit an array of Crop objects belonging to the Parcel
   */
  checkIfParcelExistInCrop(parcelId, fieldId): Observable<Crop[]> {
    if (parcelId && fieldId) {
      return this.getCrops(fieldId).pipe(
        map((crops: Crop[]) => crops.filter((c) => c.parcel && c.parcel.id === parcelId))
      );
    } else {
      // If parcelId or fieldId is not valid (e.g., undefined, null, or empty),
      // we return an empty observable array using `of([])`.
      // This ensures that the method always returns an Observable<Crop[]>,
      // even when the input data is invalid. Returning an empty array is a
      // safe way to indicate that no matching crops were found, avoiding errors
      // or the need to handle null/undefined values in the calling code.
      return of([]);
    }
  }

  /**
   * Sends the entire fields array to the bulk endpoint via HTTP POST,
   * then consumes the streaming response using a WritableStream.
   * Progress messages (and any received text) are emitted via the Observable.
   *
   * @param crops{Crop[]} An array of field objects to send.
   * @returns An Observable<string> that emits progress updates.
   */
  upsertCropsAsStream(crops: Crop[]): Observable<StreamChunk> {
    const url = `${this.apiBaseUrl}api/private/v2/crops/bulkUpsert`;

    // Map the crops to the expected format for the API.
    const requestCrops = crops.map((crop) => {
      const { commodity, field, ...rest } = crop;
      return {
        ...rest,
        commodity: { id: commodity.id },
        field: { id: field.id },
      };
    });

    return this.streamService.fetchStream(url, {
      method: 'POST',
      body: JSON.stringify(requestCrops),
    });
  }

  /**
   * Gets the Commodity associated with a Crop by its id.
   * @param cropId - The numeric identifier of the crop to look up.
   * @returns The Commodity instance associated with the crop.
   */
  getCommodityByCropId(cropId: number): Commodity {
    let currentCrop: Crop = null;
    this.cropList.pipe(first((crops: Crop[]) => !!crops?.length)).subscribe((crops: Crop[]) => {
      currentCrop = crops.filter((c) => c.id === cropId)[0];
    });
    return currentCrop.commodity;
  }
}
